import React, { useEffect, useRef } from 'react';

/**
 * The classic "Matrix" digital rain effect on a canvas.
 * Fades out after `duration` ms and calls `onDone`.
 */
const MatrixLoader = ({ duration = 2600, onDone, statusLines = [] }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const chars = 'アシプABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789$#@%&*+-=!<>[]{}';
    const fontSize = 18;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(1).map(() => Math.random() * canvas.height / fontSize);

    const draw = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const progress = Math.min(1, elapsed / duration);
      const fadeOut = progress > 0.85 ? (1 - progress) / 0.15 : 1;

      // Trailing fade
      ctx.fillStyle = 'rgba(2, 6, 23, 0.06)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `bold ${fontSize}px "IBM Plex Mono", monospace`;
      for (let i = 0; i < drops.length; i++) {
        const c = chars[Math.floor(Math.random() * chars.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;
        // Head is brighter
        ctx.fillStyle = `rgba(56, 189, 248, ${0.9 * fadeOut})`;
        ctx.fillText(c, x, y);
        // Tail
        ctx.fillStyle = `rgba(14, 165, 233, ${0.4 * fadeOut})`;
        if (y - fontSize > 0) ctx.fillText(chars[Math.floor(Math.random() * chars.length)], x, y - fontSize);
        // Reset drop
        if (y > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i] += 1;
      }

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(draw);
      } else {
        onDone && onDone();
      }
    };
    rafRef.current = requestAnimationFrame(draw);

    const onResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener('resize', onResize);
    };
  }, [duration, onDone]);

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 overflow-hidden" data-testid="matrix-loader">
      <canvas ref={canvasRef} className="absolute inset-0" />
      {/* Overlay content */}
      <div className="relative z-10 h-full flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/30 flex items-center justify-center">
              <i className="fas fa-shield-halved text-sky-400 text-xl"></i>
            </div>
            <div className="text-left leading-none">
              <p className="text-[10px] uppercase tracking-[0.35em] text-sky-400/70 font-bold">Ashop</p>
              <p className="text-xl font-bold text-white tracking-tight">Admin Portal</p>
            </div>
          </div>

          {/* Personalised welcome */}
          <div
            className="mb-6 opacity-0"
            style={{ animation: 'matrixWelcome 0.6s ease-out 0.15s forwards' }}
            data-testid="matrix-welcome"
          >
            <p className="text-slate-300 text-sm font-medium mb-1">Welcome Back! 👋</p>
            <p className="text-2xl font-bold bg-gradient-to-r from-sky-400 via-cyan-300 to-emerald-400 bg-clip-text text-transparent">
              Shahbaz Baig
            </p>
            <p className="text-slate-400 text-xs mt-1">Ready to manage your packages today</p>
          </div>

          <p className="text-emerald-400 font-mono text-sm animate-pulse mb-6">
            &gt; Initializing secure session
            <span className="inline-block ml-1 w-2 h-4 bg-emerald-400 align-middle"></span>
          </p>
          <div className="space-y-1.5 font-mono text-xs">
            {statusLines.map((line, i) => (
              <p
                key={i}
                className="text-slate-400 opacity-0"
                style={{ animation: `matrixLine 0.4s ease-out ${i * 0.25}s forwards` }}
              >
                <span className="text-sky-400">▸</span> {line}
              </p>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes matrixLine {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes matrixWelcome {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default MatrixLoader;
